import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Container,
  Button,
  TextField,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Badge,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import SyncIcon from "@mui/icons-material/Sync";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import AddBoxIcon from "@mui/icons-material/AddBox";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  useGetAllDealerSettingsMutation,
  useLazyGetSubscriptionsQuery,
  useLazyGetFeaturesQuery,
  useMarkFeatureAsAddonMutation,
  useLazyGetAddOnsQuery,
  useRemoveFeatureAsAddonMutation,
  useLazyGetAvailableFeaturesForDealerQuery,
  useAssignAddOnToDealerMutation,
  useUnAssignAddOnToDealerMutation,
} from "../../redux/reducer/features/Dealer/DealerSettingsSlice";
import AppGrid from "../app/AppGrid";
import AppConfirmDialog from "../app/AppConfirmDialog";

const DealerSettings: React.FunctionComponent = () => {
  const [dealerSettings, setDealerSettings] = useState<any[]>([]);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [featuresDialogOpen, setFeaturesDialogOpen] = useState(false);
  const [addOnsDialogOpen, setAddOnsDialogOpen] = useState(false);
  const [featureToMarkAsAddon, setFeatureToMarkAsAddon] = useState<any | null>(
    null,
  );
  const [addOnToRemove, setAddOnToRemove] = useState<any | null>(null);
  const [setupsDialogRow, setSetupsDialogRow] = useState<any | null>(null);
  const [dealerSubscriptionRow, setDealerSubscriptionRow] = useState<
    any | null
  >(null);

  const [getAllDealerSettings, getAllDealerSettingsResult] =
    useGetAllDealerSettingsMutation();
  const [
    triggerGetSubscriptions,
    { data: subscriptionsData, isFetching: isSubscriptionsLoading },
  ] = useLazyGetSubscriptionsQuery();
  const [
    triggerGetFeatures,
    { data: featuresData, isFetching: isFeaturesLoading },
  ] = useLazyGetFeaturesQuery();
  const [markFeatureAsAddon, markFeatureAsAddonResult] =
    useMarkFeatureAsAddonMutation();
  const [triggerGetAddOns, { data: addOnsData, isFetching: isAddOnsLoading }] =
    useLazyGetAddOnsQuery();
  const [removeFeatureAsAddon, removeFeatureAsAddonResult] =
    useRemoveFeatureAsAddonMutation();
  const [
    triggerGetAvailableFeaturesForDealer,
    { data: availableFeaturesData, isFetching: isAvailableFeaturesLoading },
  ] = useLazyGetAvailableFeaturesForDealerQuery();
  const [assignAddOnToDealer] = useAssignAddOnToDealerMutation();
  const [unAssignAddOnToDealer] = useUnAssignAddOnToDealerMutation();

  // Optimistic local state for the currently-open dealer's add-on assignments.
  const [assignedAddOnIds, setAssignedAddOnIds] = useState<Set<string>>(
    new Set(),
  );
  const [pendingAddOnIds, setPendingAddOnIds] = useState<Set<string>>(
    new Set(),
  );
  // Confirmation dialog state for assign / unassign
  const [addOnToToggle, setAddOnToToggle] = useState<{
    featureId: string;
    featureName: string;
    isAssigned: boolean;
  } | null>(null);

  // Seed assigned set whenever the dialog data changes.
  useEffect(() => {
    if (!dealerSubscriptionRow) {
      setAssignedAddOnIds(new Set());
      setPendingAddOnIds(new Set());
      return;
    }
    const globalAddOnFeatureIds = new Set<string>(
      (addOnsData?.entityList || []).map((a: any) => a.featureId),
    );
    const dealerFeatureIds = (availableFeaturesData?.entityList || []).map(
      (f: any) => f.id,
    );
    const assigned = new Set<string>(
      dealerFeatureIds.filter((id: string) => globalAddOnFeatureIds.has(id)),
    );
    setAssignedAddOnIds(assigned);
  }, [dealerSubscriptionRow, availableFeaturesData, addOnsData]);

  const handleRequestToggleAddOn = (featureId: string, featureName: string) => {
    if (!featureId) return;
    if (pendingAddOnIds.has(featureId)) return;
    setAddOnToToggle({
      featureId,
      featureName,
      isAssigned: assignedAddOnIds.has(featureId),
    });
  };

  const handleConfirmToggleAddOn = async () => {
    if (!addOnToToggle) return;
    const { featureId, isAssigned: isCurrentlyAssigned } = addOnToToggle;
    const dealerId = dealerSubscriptionRow?.setups?.[0]?.dealerId;
    const dealerGuid = dealerSubscriptionRow?.dealerGuid;
    setAddOnToToggle(null);
    if (!dealerId || !featureId) return;

    // Optimistic toggle
    setAssignedAddOnIds((prev) => {
      const next = new Set(prev);
      if (isCurrentlyAssigned) next.delete(featureId);
      else next.add(featureId);
      return next;
    });
    setPendingAddOnIds((prev) => {
      const next = new Set(prev);
      next.add(featureId);
      return next;
    });

    try {
      if (isCurrentlyAssigned) {
        await unAssignAddOnToDealer({ dealerId, featureId }).unwrap();
      } else {
        await assignAddOnToDealer({ dealerId, featureId }).unwrap();
      }
      // Refresh data from server after success
      if (dealerGuid) {
        triggerGetAvailableFeaturesForDealer({ dealerGuid }, false);
      }
      triggerGetAddOns(undefined, false);
    } catch (err) {
      // Revert on failure
      setAssignedAddOnIds((prev) => {
        const next = new Set(prev);
        if (isCurrentlyAssigned) next.add(featureId);
        else next.delete(featureId);
        return next;
      });
    } finally {
      setPendingAddOnIds((prev) => {
        const next = new Set(prev);
        next.delete(featureId);
        return next;
      });
    }
  };

  const fetchAllSettings = async () => {
    try {
      const response = await getAllDealerSettings().unwrap();

      if (response.status === 200) {
        setDealerSettings(response.entityList || []);
      } else {
        setDealerSettings([]);
      }
    } catch (err: any) {
      setDealerSettings([]);
    }
  };
  useEffect(() => {
    fetchAllSettings();
    triggerGetAddOns(undefined, true);
  }, []);

  const handleViewSubscriptions = () => {
    setSubscriptionDialogOpen(true);
    // Fetch only if not already cached
    triggerGetSubscriptions(undefined, true);
  };

  const handleViewFeatures = () => {
    setFeaturesDialogOpen(true);
    // Both are needed so we can filter out features already marked as add-ons
    triggerGetFeatures(undefined, true);
    triggerGetAddOns(undefined, true);
  };

  const handleRefreshFeatures = () => {
    triggerGetFeatures(undefined, false);
    triggerGetAddOns(undefined, false);
  };

  const handleConfirmMarkAsAddon = async () => {
    if (!featureToMarkAsAddon) return;
    try {
      await markFeatureAsAddon({
        featureId: featureToMarkAsAddon.id,
      }).unwrap();
      setFeatureToMarkAsAddon(null);
      triggerGetFeatures(undefined, false);
      triggerGetAddOns(undefined, false);
    } catch (err: any) {
      setFeatureToMarkAsAddon(null);
    }
  };

  const handleViewAddOns = () => {
    setAddOnsDialogOpen(true);
    triggerGetAddOns(undefined, true);
  };

  const handleConfirmRemoveAddOn = async () => {
    if (!addOnToRemove) return;
    try {
      setAddOnToRemove(null);
      await removeFeatureAsAddon({
        featureId: addOnToRemove.featureId,
      }).unwrap();
      triggerGetAddOns(undefined, false);
      triggerGetFeatures(undefined, false);
    } catch (err: any) {
      setAddOnToRemove(null);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "dmsDealerId",
      headerName: "DMS Dealer ID",
      width: 200,
      editable: false,
      valueGetter: (params: GridValueGetterParams<any>) =>
        params?.row?.dmsDealerId || "N/A",
    },
    {
      field: "subscriptionName",
      headerName: "Subscription",
      width: 200,
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row?.subscriptionName,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Chip
            label={params?.row?.subscriptionName || "N/A"}
            size="small"
            color={params?.row?.subscriptionName ? "primary" : "default"}
            variant="outlined"
            clickable={!!params?.row?.subscriptionName}
            onClick={
              params?.row?.subscriptionName
                ? () => {
                    setDealerSubscriptionRow(params.row);
                    if (params.row?.dealerGuid) {
                      triggerGetAvailableFeaturesForDealer(
                        { dealerGuid: params.row.dealerGuid },
                        true,
                      );
                    }
                  }
                : undefined
            }
          />
        );
      },
    },
    {
      field: "setups",
      headerName: "Setups",
      width: 150,
      type: "actions",
      renderCell: (params: GridRenderCellParams) => {
        const count = params?.row?.setups?.length ?? 0;
        return (
          <Tooltip title="View Setups">
            <span>
              <IconButton
                size="small"
                color="primary"
                onClick={() => setSetupsDialogRow(params.row)}
                disabled={count === 0}
              >
                <Badge
                  badgeContent={count}
                  color={count > 1 ? "warning" : "error"}
                  showZero
                  sx={{ fontSize: "small" }}
                >
                  <VisibilityIcon />
                </Badge>
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Container maxWidth={false}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Box display="flex" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: "bold" }} gutterBottom>
            Dealer Settings
          </Typography>
          <IconButton onClick={() => fetchAllSettings()}>
            <SyncIcon />
          </IconButton>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="contained"
            color="info"
            size="small"
            onClick={handleViewFeatures}
          >
            View Features
          </Button>
          <Button
            variant="contained"
            color="warning"
            size="small"
            onClick={handleViewAddOns}
          >
            View Add-ons
          </Button>
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={handleViewSubscriptions}
          >
            View Subscriptions
          </Button>
        </Box>
      </Box>

      <AppGrid
        data={[...dealerSettings].sort((a: any, b: any) => {
          return (
            (b.hasMultipleSite === true ? 1 : 0) -
            (a.hasMultipleSite === true ? 1 : 0)
          );
        })}
        isLoading={getAllDealerSettingsResult.isLoading}
        columns={columns}
        checkBox={true}
        height={"80vh"}
      ></AppGrid>
      <Dialog
        open={subscriptionDialogOpen}
        onClose={() => setSubscriptionDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: "40vh" } }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            Subscriptions
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => triggerGetSubscriptions(undefined, false)}
                disabled={isSubscriptionsLoading}
                size="small"
              >
                <SyncIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <IconButton onClick={() => setSubscriptionDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {isSubscriptionsLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="30vh"
            >
              <CircularProgress />
            </Box>
          ) : !subscriptionsData?.entityList ||
            subscriptionsData.entityList.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No subscriptions found.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {[...subscriptionsData.entityList]
                .sort(
                  (a: any, b: any) =>
                    (a.features?.length ?? 0) - (b.features?.length ?? 0),
                )
                .map((sub: any, index: number) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardHeader
                        title={sub.subscriptionName}
                        titleTypographyProps={{
                          variant: "subtitle1",
                          fontWeight: "bold",
                        }}
                        sx={{
                          bgcolor: "action.hover",
                        }}
                      />
                      <CardContent>
                        <List dense disablePadding>
                          {sub.features?.length > 0 ? (
                            sub.features.map((feature: any, fIndex: number) => (
                              <ListItem disableGutters key={fIndex}>
                                <ListItemIcon sx={{ minWidth: 28 }}>
                                  <CheckCircleIcon
                                    fontSize="small"
                                    color="success"
                                  />
                                </ListItemIcon>
                                <ListItemText primary={feature.featureName} />
                              </ListItem>
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No features available.
                            </Typography>
                          )}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
            </Grid>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={featuresDialogOpen}
        onClose={() => setFeaturesDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: "40vh" } }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            Available Features
            <Tooltip title="Refresh">
              <IconButton
                onClick={handleRefreshFeatures}
                disabled={isFeaturesLoading}
                size="small"
              >
                <SyncIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <IconButton onClick={() => setFeaturesDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {isFeaturesLoading || isAddOnsLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="30vh"
            >
              <CircularProgress />
            </Box>
          ) : !featuresData?.entityList ||
            featuresData.entityList.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No features found.
            </Typography>
          ) : (
            (() => {
              const addOnFeatureIds = new Set(
                (addOnsData?.entityList || []).map((a: any) => a.featureId),
              );
              const visibleFeatures = featuresData.entityList.filter(
                (f: any) => !addOnFeatureIds.has(f.id),
              );
              if (visibleFeatures.length === 0) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    No features found.
                  </Typography>
                );
              }
              return (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell sx={{ fontWeight: "bold", width: 60 }}>
                          #
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Feature Name
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Description
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: "bold", width: 120 }}
                          align="center"
                        >
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleFeatures.map((feature: any, index: number) => (
                        <TableRow key={feature.id ?? index} hover>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            {(feature.productFeatureName || "").trim()}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {feature.description || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Move to Add-on">
                              <span>
                                <IconButton
                                  color="primary"
                                  onClick={() =>
                                    setFeatureToMarkAsAddon(feature)
                                  }
                                  disabled={markFeatureAsAddonResult.isLoading}
                                >
                                  <AddBoxIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              );
            })()
          )}
        </DialogContent>
      </Dialog>
      <AppConfirmDialog
        isOpen={!!featureToMarkAsAddon}
        handleClose={() => setFeatureToMarkAsAddon(null)}
        onFail={() => setFeatureToMarkAsAddon(null)}
        onSuccess={handleConfirmMarkAsAddon}
        headerText="Move Feature to Add-on"
        bodyText={
          <>
            This feature{" "}
            <strong>
              {(featureToMarkAsAddon?.productFeatureName || "").trim()}
            </strong>{" "}
            will be removed from any subscription it is part of, and will be
            available as an add-on. Do you want to continue?
          </>
        }
      />
      <Dialog
        open={addOnsDialogOpen}
        onClose={() => setAddOnsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: "40vh" } }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            Add-ons
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => triggerGetAddOns(undefined, false)}
                disabled={isAddOnsLoading}
                size="small"
              >
                <SyncIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <IconButton onClick={() => setAddOnsDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {isAddOnsLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="30vh"
            >
              <CircularProgress />
            </Box>
          ) : !addOnsData?.entityList || addOnsData.entityList.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No add-ons found.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    <TableCell sx={{ fontWeight: "bold", width: 60 }}>
                      #
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Feature Name
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: "bold", width: 120 }}
                      align="center"
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {addOnsData.entityList.map((addOn: any, index: number) => (
                    <TableRow key={addOn.id ?? index} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{(addOn.featureName || "").trim()}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Remove from Add-ons">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setAddOnToRemove(addOn)}
                              disabled={removeFeatureAsAddonResult.isLoading}
                            >
                              <RemoveCircleOutlineIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
      <AppConfirmDialog
        isOpen={!!addOnToRemove}
        handleClose={() => setAddOnToRemove(null)}
        onFail={() => setAddOnToRemove(null)}
        onSuccess={handleConfirmRemoveAddOn}
        headerText="Remove Feature from Add-ons"
        bodyText={
          <>
            The add-on{" "}
            <strong>{(addOnToRemove?.featureName || "").trim()}</strong> will be
            removed from add-ons and will be available again in the Features
            list. Do you want to continue?
          </>
        }
      />
      <Dialog
        open={!!setupsDialogRow}
        onClose={() => setSetupsDialogRow(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: "40vh" } }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            Setups
            {setupsDialogRow?.dmsDealerId && (
              <Chip
                size="small"
                label={`DMS Dealer ID: ${setupsDialogRow.dmsDealerId}`}
              />
            )}
          </Box>
          <IconButton onClick={() => setSetupsDialogRow(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {!setupsDialogRow?.setups || setupsDialogRow.setups.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No setups found.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    <TableCell sx={{ fontWeight: "bold", width: 60 }}>
                      #
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Dealer ID</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Site Domain
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {setupsDialogRow.setups.map((setup: any, index: number) => (
                    <TableRow key={setup.id ?? index} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{setup.dealerId ?? "-"}</TableCell>
                      <TableCell>{setup.setupType ?? "-"}</TableCell>
                      <TableCell>{setup.siteDomain ?? "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!dealerSubscriptionRow}
        onClose={() => setDealerSubscriptionRow(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: "40vh" } }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            Subscription
            {dealerSubscriptionRow?.subscriptionName && (
              <Chip
                size="small"
                color="primary"
                label={dealerSubscriptionRow.subscriptionName}
              />
            )}
            {dealerSubscriptionRow?.dmsDealerId != null && (
              <Chip
                size="small"
                variant="outlined"
                label={`DMS Dealer ID: ${dealerSubscriptionRow.dmsDealerId}`}
              />
            )}
            <Tooltip title="Refresh available features">
              <span>
                <IconButton
                  onClick={() => {
                    if (dealerSubscriptionRow?.dealerGuid) {
                      triggerGetAvailableFeaturesForDealer(
                        { dealerGuid: dealerSubscriptionRow.dealerGuid },
                        false,
                      );
                    }
                  }}
                  disabled={
                    isAvailableFeaturesLoading ||
                    !dealerSubscriptionRow?.dealerGuid
                  }
                >
                  <SyncIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <IconButton onClick={() => setDealerSubscriptionRow(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {isAvailableFeaturesLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="30vh"
            >
              <CircularProgress />
            </Box>
          ) : (
            (() => {
              const allDealerItems: any[] =
                availableFeaturesData?.entityList ?? [];
              const globalAddOnFeatureIds = new Set<string>(
                (addOnsData?.entityList || []).map((a: any) => a.featureId),
              );
              // Features = dealer items that are NOT add-ons
              const features = allDealerItems.filter(
                (f: any) => !globalAddOnFeatureIds.has(f.id),
              );
              // Build a lookup of descriptions from the dealer's feature list
              // (so assigned add-ons still get a description on hover).
              const descriptionByFeatureId = new Map<string, string>(
                allDealerItems.map((f: any) => [f.id, f.description || ""]),
              );
              // Add-ons section = ALL global add-ons; status is driven by the
              // optimistic `assignedAddOnIds` set.
              const allAddOns: any[] = [...(addOnsData?.entityList ?? [])].sort(
                (a: any, b: any) => {
                  const aAssigned = assignedAddOnIds.has(a.featureId) ? 1 : 0;
                  const bAssigned = assignedAddOnIds.has(b.featureId) ? 1 : 0;
                  return bAssigned - aAssigned;
                },
              );

              const renderFeatureChips = (items: any[], emptyText: string) =>
                items.length > 0 ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {items.map((item: any, index: number) => (
                      <Tooltip
                        key={item.id ?? index}
                        title={item.description || ""}
                        arrow
                      >
                        <Chip
                          label={(item.name || "").trim() || "-"}
                          variant="outlined"
                          color="primary"
                        />
                      </Tooltip>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {emptyText}
                  </Typography>
                );

              const renderAddOnChips = () =>
                allAddOns.length > 0 ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {allAddOns.map((addOn: any, index: number) => {
                      const featureId: string = addOn.featureId;
                      const isAssigned = assignedAddOnIds.has(featureId);
                      const isPending = pendingAddOnIds.has(featureId);
                      const description =
                        descriptionByFeatureId.get(featureId) || "";
                      const tooltipTitle = isPending
                        ? "Saving…"
                        : description
                          ? `${description}${isAssigned ? " — click to unassign" : " — click to assign"}`
                          : isAssigned
                            ? "Click to unassign"
                            : "Click to assign";
                      return (
                        <Tooltip
                          key={addOn.id ?? featureId ?? index}
                          title={tooltipTitle}
                          arrow
                        >
                          <Chip
                            label={(addOn.featureName || "").trim() || "-"}
                            variant={isAssigned ? "filled" : "outlined"}
                            color={isAssigned ? "success" : "default"}
                            onClick={
                              isPending
                                ? undefined
                                : () =>
                                    handleRequestToggleAddOn(
                                      featureId,
                                      addOn.featureName,
                                    )
                            }
                            onDelete={
                              isPending
                                ? undefined
                                : () =>
                                    handleRequestToggleAddOn(
                                      featureId,
                                      addOn.featureName,
                                    )
                            }
                            deleteIcon={
                              isPending ? (
                                <CircularProgress size={14} />
                              ) : isAssigned ? (
                                <CloseIcon />
                              ) : (
                                <AddCircleOutlineIcon />
                              )
                            }
                            disabled={isPending}
                            sx={{ cursor: isPending ? "default" : "pointer" }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No add-ons available.
                  </Typography>
                );

              return (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      Features
                    </Typography>
                    {renderFeatureChips(features, "No features available.")}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      Add-ons
                    </Typography>
                    {renderAddOnChips()}
                  </Grid>
                </Grid>
              );
            })()
          )}
        </DialogContent>
      </Dialog>
      <AppConfirmDialog
        isOpen={!!addOnToToggle}
        handleClose={() => setAddOnToToggle(null)}
        onFail={() => setAddOnToToggle(null)}
        onSuccess={handleConfirmToggleAddOn}
        headerText={
          addOnToToggle?.isAssigned ? "Unassign Add-on" : "Assign Add-on"
        }
        bodyText={
          <>
            Are you sure you want to{" "}
            {addOnToToggle?.isAssigned ? "unassign" : "assign"} the add-on{" "}
            <strong>
              {(addOnToToggle?.featureName || "").trim() || "this add-on"}
            </strong>{" "}
            {addOnToToggle?.isAssigned ? "from" : "to"} this dealer?
          </>
        }
      />
    </Container>
  );
};

export default DealerSettings;
